import { useTracking } from '@vp/react-tracking';
import { FC, useCallback, useEffect, useState } from 'react';
import { isFeatureEnabled } from '@vp/ab-reader';
import {
    Box,
    Button,
    ModalDialog,
    ModalDialogBody,
    ModalDialogButtons,
    ModalDialogCloseButton,
    ModalDialogContent,
    ModalDialogFooter,
    ModalDialogHeader,
    ModalDialogNav,
    ModalDialogTitle,
    useScreenClass,
} from '@vp/swan';
import { useIdentityContext } from '@99designs/design-services-common';
import { __, __url } from '@99designs/i18n';
import { TopBar } from '@99designs/swan-wizard';
import { usePage } from '@99designs/tracking';
import { useBriefContext } from '../BriefContext';

export interface MultiStepTopBarProps {
    title: string;
    exitUrl?: string;
    hideExitPopUp?: boolean;
}

export function MultiStepTopBar({ title, exitUrl, hideExitPopUp }: MultiStepTopBarProps) {
    const { isBifrostBrief } = useBriefContext();
    let exitButton = <Box pr={8} />;

    // TODO: Remove isBifrostBrief check once all bifrost products are removed
    if (isFeatureEnabled('design_services_draft_briefs') && !isBifrostBrief) {
        exitButton = <SaveAndExitButton />;
    } else if (exitUrl) {
        exitButton = <ExitButton url={exitUrl} hideExitPopUp={hideExitPopUp || false} />;
    }

    return <TopBar title={title} exitButton={exitButton} />;
}

interface ExitButtonProps {
    url: string;
    hideExitPopUp: boolean;
}

export const ExitButton: FC<ExitButtonProps> = ({ url, hideExitPopUp }) => {
    const { pageData } = usePage();
    const { tracking } = useTracking();

    const screenClass = useScreenClass();

    const [isOpen, setIsOpen] = useState(false);

    const openExitModal = () => {
        setIsOpen(true);
    };

    const closeExitModal = () => {
        tracking.track('Pop-Up Closed', {
            category: 'Pop-Up',
            label: 'Design Brief Exit Modal Pop-Up',
            pageSection: pageData.pageSection,
            pageStage: pageData.pageStage,
            pageName: pageData.pageName,
        });
        setIsOpen(false);
    };

    const handleExitConfirmation = useCallback(() => {
        tracking.track('Navigation Clicked', {
            category: pageData.pageSection,
            label: 'Content: Exit Confirmation Pop-Up',
            eventDetail: `${window.location.pathname}${window.location.search};${url};Exit Confirmation Pop-Up;designBriefExitConfirmationButton`,
            navigationDetail: 'designBriefExitConfirmationButton',
            pageSection: pageData.pageSection,
            pageStage: pageData.pageStage,
            pageName: pageData.pageName,
        });

        window.location.href = url;
    }, [pageData, tracking, url]);

    useEffect(() => {
        if (isOpen) {
            tracking.track('Pop-Up Viewed', {
                category: 'Pop-Up',
                label: 'Design Brief Exit Modal Pop-Up',
                pageSection: pageData.pageSection,
                pageStage: pageData.pageStage,
                pageName: pageData.pageName,
            });
        }
    }, [isOpen, pageData, tracking]);

    return (
        <>
            <Button
                skin={screenClass === 'xs' ? 'tertiary' : undefined}
                onClick={hideExitPopUp ? handleExitConfirmation : () => openExitModal()}
            >
                {__('Exit')}
            </Button>
            <ModalDialog isOpen={isOpen} onRequestDismiss={closeExitModal}>
                <ModalDialogContent aria-labelledby="modal dialog button">
                    <ModalDialogNav>
                        <ModalDialogCloseButton accessibleText={__('Close')} />
                    </ModalDialogNav>
                    <ModalDialogHeader>
                        <ModalDialogTitle>{__('Are you sure?')}</ModalDialogTitle>
                    </ModalDialogHeader>
                    <ModalDialogBody>
                        {__('Are you sure you want to exit your brief? All progress will be lost.')}
                    </ModalDialogBody>
                    <ModalDialogFooter>
                        <ModalDialogButtons>
                            <Button skin="secondary" onClick={closeExitModal}>
                                {__('Go back')}
                            </Button>
                            <Button onClick={handleExitConfirmation} skin="primary">
                                {__('Yes, exit')}
                            </Button>
                        </ModalDialogButtons>
                    </ModalDialogFooter>
                </ModalDialogContent>
            </ModalDialog>
        </>
    );
};

function SaveAndExitButton() {
    const { SignIn, isSignedIn } = useIdentityContext();

    const { pageData } = usePage();
    const { tracking } = useTracking();

    const screenClass = useScreenClass();

    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (isOpen) {
            tracking.track('Pop-Up Viewed', {
                category: 'Pop-Up',
                label: 'Design Brief Exit Modal Pop-Up',
                pageSection: pageData.pageSection,
                pageStage: pageData.pageStage,
                pageName: pageData.pageName,
            });
        }
    }, [isOpen, pageData, tracking]);

    const handleExit = useCallback(
        (isInModal?: boolean) => {
            const dashboardUrl = __url('/graphic-design/dashboard');

            if (isInModal) {
                tracking.track('Navigation Clicked', {
                    category: pageData.pageSection,
                    label: 'Content: Exit Confirmation Pop-Up',
                    eventDetail: `${window.location.pathname}${window.location.search};${dashboardUrl};Exit Confirmation Pop-Up;designBriefExitConfirmationButton`,
                    navigationDetail: 'designBriefExitConfirmationButton',
                    pageSection: pageData.pageSection,
                    pageStage: pageData.pageStage,
                    pageName: pageData.pageName,
                });
            } else {
                tracking.track('Navigation Clicked', {
                    category: pageData.pageSection,
                    label: 'Design Brief Exit To Drafts',
                    eventDetail: `${window.location.pathname}${window.location.search};${dashboardUrl};Design Brief Exit To Drafts;designBriefExitToDrafts`,
                    navigationDetail: 'designBriefExitToDrafts',
                    pageSection: pageData.pageSection,
                    pageStage: pageData.pageStage,
                    pageName: pageData.pageName,
                });
            }

            window.location.href = dashboardUrl;
        },
        [pageData, tracking]
    );

    const handleSaveAndExitButton = () => {
        // Saving as a draft is already done progressively as the user fills in the form.

        if (!isSignedIn) {
            setIsOpen(true);
        } else {
            handleExit();
        }
    };

    const handleCancel = () => {
        tracking.track('Pop-Up Closed', {
            category: 'Pop-Up',
            label: 'Design Brief Exit Modal Pop-Up',
            pageSection: pageData.pageSection,
            pageStage: pageData.pageStage,
            pageName: pageData.pageName,
        });

        setIsOpen(false);
    };

    return (
        <>
            <Button
                skin={screenClass === 'xs' ? 'tertiary' : undefined}
                onClick={handleSaveAndExitButton}
            >
                {__('Save & exit', {
                    __context:
                        'This is text on the escape hatch/exit button for a design brief form.',
                })}
            </Button>
            <ModalDialog isOpen={isOpen} onRequestDismiss={handleCancel}>
                <ModalDialogContent aria-labelledby="modal dialog button">
                    <ModalDialogNav>
                        <ModalDialogCloseButton accessibleText={__('Close')} />
                    </ModalDialogNav>
                    <ModalDialogHeader>
                        <ModalDialogTitle>
                            {__('Save your progress', {
                                __context:
                                    'This is the title text for the confirmation modal when exiting a brief when not signed in.',
                            })}
                        </ModalDialogTitle>
                    </ModalDialogHeader>
                    <ModalDialogBody>
                        {__('Sign in to save this brief as a draft or exit without saving.', {
                            __context:
                                'This is the body text for the confirmation modal when exiting a brief when not signed in.',
                        })}
                    </ModalDialogBody>
                    <ModalDialogFooter>
                        <ModalDialogButtons>
                            <Button skin="secondary" onClick={() => handleExit(true)}>
                                {__('Exit without saving', {
                                    __context:
                                        'This is the text on a modal button shown to signed-out or anonymous users indicating that they wish to discard their form progress.',
                                })}
                            </Button>
                            <Button
                                onClick={() => SignIn([{ key: 'isRedirectToDrafts', value: '1' }])}
                                skin="primary"
                            >
                                {__('Sign in and save', {
                                    __context:
                                        'This is the text on a modal button shown to signed-out or anonymous users indicating that they wish to sign in to save their draft brief form.',
                                })}
                            </Button>
                        </ModalDialogButtons>
                    </ModalDialogFooter>
                </ModalDialogContent>
            </ModalDialog>
        </>
    );
}
